import { useState, useCallback, useMemo } from "react";
import { Button, Dialog, Flex, Text, TextField } from "@radix-ui/themes";
import { useBrandApi } from "@/v2/entities/brand/Brand.api";
import { UpsertBrandInputDto } from "@/v2/entities/brand/Brand.model";

interface CreateBrandDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  refetch: () => void;
}

export function CreateBrandDialog({
  open,
  onOpenChange,
  refetch,
}: CreateBrandDialogProps) {
  const brandApi = useBrandApi();

  const [formData, setFormData] = useState<UpsertBrandInputDto>({
    name: "",
    subdomain: "",
  });
  const [errors, setErrors] = useState<Partial<UpsertBrandInputDto>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<UpsertBrandInputDto> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Brand name is required";
    }

    if (!formData.subdomain.trim()) {
      newErrors.subdomain = "Subdomain is required";
    } else if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
      newErrors.subdomain =
        "Subdomain can only contain lowercase letters, numbers, and hyphens";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await brandApi.createBrand(formData);
      setFormData({ name: "", subdomain: "" });
      setErrors({});
      onOpenChange(false);
      refetch();
    } catch (error) {
      // error is handled by the mutation
      // eslint-disable-next-line no-console
      console.error("failed to create brand:", error);
    } finally {
      setIsLoading(false);
    }
  }, [formData, validateForm, brandApi, onOpenChange, refetch]);

  const handleCancel = useCallback(() => {
    setFormData({ name: "", subdomain: "" });
    setErrors({});
    onOpenChange(false);
  }, [onOpenChange]);

  const handleFieldChange = useCallback(
    (field: keyof UpsertBrandInputDto, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error for this field when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [errors]
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="450px">
        <Dialog.Title>Create Brand</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Create a new brand for your tenant.
        </Dialog.Description>

        <Flex direction="column" gap="3">
          <label>
            <Text as="div" size="2" mb="1" weight="bold">
              Brand Name
            </Text>
            <TextField.Root
              placeholder="Enter brand name"
              value={formData.name}
              onChange={(e) => handleFieldChange("name", e.target.value)}
            />
            {errors.name && (
              <Text size="1" color="red" mt="1">
                {errors.name}
              </Text>
            )}
          </label>

          <label>
            <Text as="div" size="2" mb="1" weight="bold">
              Subdomain
            </Text>
            <TextField.Root
              placeholder="Enter subdomain (e.g., mybrand)"
              value={formData.subdomain}
              onChange={(e) =>
                handleFieldChange("subdomain", e.target.value.toLowerCase())
              }
            />
            {errors.subdomain && (
              <Text size="1" color="red" mt="1">
                {errors.subdomain}
              </Text>
            )}
          </label>
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" onClick={handleCancel}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button onClick={handleSubmit} loading={isLoading} highContrast>
            Create Brand
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
